import { ReactElement, useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  useToast,
  Container,
  Heading,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { DB } from "../utils/db";
import { decryptData } from "../utils/crypto";
import { BasicPageLayout } from "../components/templates/BasicPageLayout";
import { useDefaultPath } from "../hooks/useDefaultPath";

// ファイルアップロード用のヘルパー関数
function uploadFile(): Promise<string> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";

    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) {
        reject(new Error("No file selected"));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        resolve(content);
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsText(file);
    };

    input.click();
  });
}

export function DevImportUsers(): ReactElement {
  const [passphrase, setPassphrase] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const defaultPath = useDefaultPath();

  const handleImport = async (): Promise<void> => {
    if (!passphrase || passphrase.length < 4) {
      toast({
        title: "パスフレーズエラー",
        description: "パスフレーズは4文字以上で指定してください",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const fileContent = await uploadFile();
      const fileData = JSON.parse(fileContent);

      // 暗号化されたファイルかチェック
      if (!fileData.encrypted || !fileData.data) {
        throw new Error("無効なファイル形式です");
      }

      // パスフレーズで復号化
      const decryptedJson = await decryptData(fileData.data, passphrase);
      const result = await DB.importUsersFromJson(decryptedJson);

      toast({
        title: "インポート完了",
        description: `${result.imported}件追加, ${result.skipped}件スキップ（既存）`,
        status: "success",
        duration: 3000,
        isClosable: true,
        onCloseComplete: () => {
          navigate(defaultPath);
        },
      });
      setPassphrase("");
    } catch (error) {
      toast({
        title: "インポート失敗",
        description:
          error instanceof Error ? error.message : "不明なエラーが発生しました",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BasicPageLayout title="ユーザーインポート">
      <Container maxW="container.sm" py={8}>
        <VStack spacing={6} align="stretch">
          <Heading size="md">ユーザーデータインポート</Heading>
          <Text fontSize="sm" color="gray.600">
            サービスマン専用機能
          </Text>

          <FormControl>
            <FormLabel>合言葉</FormLabel>
            <Input
              type="password"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              placeholder="合言葉を入力"
            />
          </FormControl>

          <Button
            colorScheme="blue"
            onClick={handleImport}
            isLoading={isLoading}
            isDisabled={!passphrase}
          >
            JSONファイルを選択してインポート
          </Button>
        </VStack>
      </Container>
    </BasicPageLayout>
  );
}
